import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, User, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "../theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const { isDark } = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error("Please enter username and password");
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        username: username.trim(),
        password: password.trim(),
      });
      localStorage.setItem("neuromap_token", res.data.access_token);
      toast.success("Welcome back!");
      navigate("/portal-d7glqibsq4/dashboard");
    } catch (err) {
      const message = err.response?.data?.detail || "Invalid credentials";
      toast.error(message);
    }
    setIsLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: isDark ? "#0f172a" : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #fce7f3 100%)",
      }}
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 mb-8 font-medium transition-colors"
          data-testid="back-to-map-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Map
        </Link>

        {/* Login Card */}
        <div
          className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl p-8 border border-white/50"
          data-testid="login-card"
        >
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1
                className="text-2xl font-black text-slate-800"
                style={{ fontFamily: "Nunito" }}
              >
                Labelled
              </h1>
              <p className="text-slate-500 text-sm">Admin Portal</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="input-field"
                data-testid="username-input"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="input-field"
                data-testid="password-input"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full mt-6"
              data-testid="login-submit-btn"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
